import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Link2, Loader2 } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { organisationsAPI } from '../../services/api';
import toast from '../../services/toast';
import { contributorRowAvatarUrl } from '../../utils/githubAvatarUrl';

export default function MemberGitHubSuggestModal({ isOpen, onClose, orgId, onLinked }: any) {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [linkingId, setLinkingId] = useState<any>(null);
  const [skipped, setSkipped] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const list = await organisationsAPI.suggestMemberGitHubLinks(orgId);
      setSuggestions(Array.isArray(list) ? list : []);
      setSkipped(new Set());
    } catch (e) {
      toast.error((e as Error).message || 'Could not load suggestions');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (isOpen && orgId) load();
  }, [isOpen, orgId, load]);

  const visible = suggestions.filter((s) => !skipped.has(s.userId));

  const handleLink = async (row) => {
    if (!orgId) return;
    setLinkingId(row.userId);
    try {
      await organisationsAPI.linkMemberToGitHub(orgId, row.userId, {
        githubUsername: row.githubUsername,
      });
      toast.success(`Linked ${row.memberName || row.memberEmail} to @${row.githubUsername}`);
      setSuggestions((prev) => prev.filter((s) => s.userId !== row.userId));
      onLinked?.();
    } catch (e) {
      toast.error((e as Error).message || 'Link failed');
    } finally {
      setLinkingId(null);
    }
  };

  const handleSkip = (userId) => {
    setSkipped((prev) => new Set(prev).add(userId));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Suggest GitHub matches"
      icon={<Link2 className="w-5 h-5 text-vivid-red" />}
      contentClassName="max-w-3xl"
    >
      <div className="p-4 max-h-[70vh] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden />
          </div>
        ) : visible.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">
            {suggestions.length === 0
              ? 'No suggestions — all members may already be linked, or no close name/email matches were found in your repositories.'
              : 'No more suggestions in this list.'}
          </p>
        ) : (
          <ul className="space-y-3">
            {visible.map((row) => {
              const avatarSrc = contributorRowAvatarUrl({
                githubUsername: row.githubUsername,
                avatarUrl: row.avatarUrl,
              });
              const busy = linkingId === row.userId;
              return (
                <li
                  key={`${row.userId}-${row.contributorId}`}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-dark-border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-100">
                      {row.memberName || row.memberEmail}
                    </div>
                    {row.memberEmail ? (
                      <div className="text-xs text-slate-400">{row.memberEmail}</div>
                    ) : null}
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt=""
                        className="h-8 w-8 rounded-full object-cover"
                        width={32}
                        height={32}
                      />
                    ) : null}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-100 truncate">
                        {row.contributorName || `@${row.githubUsername}`}
                      </div>
                      <div className="text-xs text-slate-400">@{row.githubUsername}</div>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                      row.confidence === 'high'
                        ? 'bg-green-900/40 text-green-200'
                        : 'bg-amber-900/40 text-amber-200'
                    }`}
                  >
                    {row.confidence === 'high' ? 'High' : 'Medium'}
                  </span>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      className="btn-primary-mdl text-xs"
                      disabled={busy}
                      onClick={() => handleLink(row)}
                    >
                      {busy ? 'Linking…' : 'Link'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary-mdl text-xs"
                      disabled={busy}
                      onClick={() => handleSkip(row.userId)}
                    >
                      Skip
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}
